import React, { FC, useCallback, useMemo } from "react";
import { Color } from "@evergis/color";
import { useTranslation } from "react-i18next";

import { MaskedImageSymbol, isMaskedImageSymbol, ImageSymbol } from "../../../interfaces/style";
import { useSymbolSettingsContext } from "../../../modules/Style/context/styleSettingsContext";
import { ColorClassifier } from "./Classifiers/ColorClassifier";
import { NumberClassifier } from "./Classifiers/NumberClassifier";
import { getParameterValue } from "../../../evergis/core/classification/getFromClassified";
import { EditorContainer, InputsGroup } from "../styled";
import { getOffsetParameterValue } from "../../../evergis/core/classification/classifyOffset";
import { StyleEditorProps } from "../SymbolEditor";
import { AngleClassifier } from "./Classifiers/AngleClassifier";
import { useAngleChange } from "../hooks/useAngleChange";

export const ImageSymbolEditor: FC<StyleEditorProps<ImageSymbol>> = ({ symbol, onChange }) => {
  const { t } = useTranslation("common");
  const { mini } = useSymbolSettingsContext();

  const handleChangeColor = useCallback(
    (value: Color) => {
      onChange({
        ...symbol,
        maskedColor: value.toString("hex"),
      } as MaskedImageSymbol);
    },
    [onChange, symbol],
  );

  const handleChangeSize = useCallback(
    (value: number) => {
      const { offset } = symbol;

      onChange({
        ...symbol,
        ...getDimensions(symbol, value),
        offset: getOffsetParameterValue(offset),
      });
    },
    [onChange, symbol],
  );

  const handleChangeAngle = useAngleChange(symbol, onChange);

  const size = useMemo(() => getSizeParameter(symbol.width, symbol.height), [symbol.width, symbol.height]);

  return (
    <EditorContainer>
      <InputsGroup
        mini={mini}
        label={isMaskedImageSymbol(symbol) ? t("symbolEditor.sizeAndColor") : t("symbolEditor.size")}
      >
        <NumberClassifier name={"size"} value={size} onChange={handleChangeSize} />
        {isMaskedImageSymbol(symbol) && (
          <ColorClassifier name={"maskedColor"} value={symbol.maskedColor} onChange={handleChangeColor} />
        )}
      </InputsGroup>
      <InputsGroup mini={mini} label={t("symbolEditor.rotation")}>
        <AngleClassifier name={"angle"} value={symbol.angle} onChange={handleChangeAngle} />
      </InputsGroup>
    </EditorContainer>
  );
};

function getSizeParameter(width, height): number {
  const widthValue = getParameterValue(width);
  const heightValue = getParameterValue(height);

  if (widthValue < heightValue) {
    return width;
  }

  return height;
}

type Dimensions = {
  height: number;
  width: number;
};

export function getDimensions(symbol: ImageSymbol, size: number): Dimensions {
  const height = getParameterValue(symbol.height);
  const width = getParameterValue(symbol.width);

  if (width === height) {
    return {
      width: size,
      height: size,
    };
  }

  const ratio = height / width;

  if (width < height) {
    return {
      width: size,
      height: height + (size - width) * ratio,
    };
  }

  return {
    width: width + (size - height) * ratio,
    height: size,
  };
}
